import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { usePatient } from '../../hooks/usePatient';
import { useMessagesThread, useMessageConfirmedStatus, useSendMessage } from '../../hooks/api';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { PageDataBoundary } from '../../components/PageDataBoundary/PageDataBoundary';
import { PatientSwitcher } from '../../components/PatientSwitcher';
import { EmptyState, ReplyBottomSheet, SendSuccessDialog } from '../../components';
import { ModeTabs } from './components/ModeTabs';
import type { MessageMode } from './components/ModeTabs';
import { ComposeView } from './components/ComposeView';
import { HistoryView } from './components/HistoryView';
import type { ReplyTo } from '../../lib/api';
import { isMessageSendingClosed } from '../../utils';
import {
  Container,
  ContentArea,
} from './MessagePage.styled';

const getModeFromHash = (hash: string): MessageMode | null => {
  if (hash === '#history') return 'history';
  if (hash === '#compose') return 'compose';
  return null;
};

interface LocationState {
  replyTo?: ReplyTo;
  initialMode?: MessageMode;
}

export const MessagePage: React.FC = () => {
  const {
    selectedPatientId,
    selectedPatientIndex,
    linkedPatients,
    selectPatientByIndex,
    selectedPatient,
  } = usePatient();

  // Location state for reply and initial mode
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState | null;
  const initialReplyTo = locationState?.replyTo;

  const modeFromHash = getModeFromHash(location.hash);

  /**
   * 通知からの遷移（B-10）。`patient_id` による利用者の選択は `PatientContext` が
   * 既に担っているので、ここで見るのは位置合わせに使う 2 つだけ。
   *
   * **利用者の選択とスレッドの特定は独立して効く**（B-10 #3）— スレッドが見つからなくても
   * エラーにせず、通常どおりやりとり画面を開く。
   */
  const [searchParams] = useSearchParams();
  const focusThreadId = searchParams.get('thread_id');
  const focusMessageId = Number(searchParams.get('message_id')) || null;
  const hasNotificationTarget = focusMessageId !== null || focusThreadId !== null;

  // 通知から開いたときは、入力ではなくやりとりを出す
  const initialMode = modeFromHash || locationState?.initialMode || (hasNotificationTarget ? 'history' : undefined);

  const isSendingClosed = isMessageSendingClosed(selectedPatient);

  const [activeMode, setActiveMode] = useState<MessageMode>(
    isSendingClosed ? 'history' : initialMode || 'compose'
  );
  const [messageText, setMessageText] = useState('');
  const [hideFromPatient, setHideFromPatient] = useState(false);
  const [showSendSuccessDialog, setShowSendSuccessDialog] = useState(false);
  const [replyTo, setReplyTo] = useState<ReplyTo | null>(initialReplyTo || null);
  /**
   * 返信先スレッド。値があるあいだ返信用の BottomSheet を開く（B-8 #5）。
   * 入力タブからの送信はスレッドを選ばず、従来どおり新規スレッドを作る（B-8 #6）。
   */
  const [replyTargetThreadId, setReplyTargetThreadId] = useState<string | null>(null);
  const [replyMessageText, setReplyMessageText] = useState('');
  /** 下書きを引き継ぐのは同じスレッドを開き直したときだけにする（別スレッドへ本文を持ち越さない） */
  const lastReplyThreadIdRef = useRef<string | null>(null);
  // 送信成功のコールバックは送信時点の値を閉じ込めるため、その時点の最新状態を参照できるようにする
  const replyTargetThreadIdRef = useRef(replyTargetThreadId);
  replyTargetThreadIdRef.current = replyTargetThreadId;
  const replyMessageTextRef = useRef(replyMessageText);
  replyMessageTextRef.current = replyMessageText;
  const [isInputFocused, setIsInputFocused] = useState(false);

  // キーボード表示状態をbody data属性に同期（BottomNavigation非表示用）
  useEffect(() => {
    if (isInputFocused) {
      document.body.dataset.keyboardOpen = 'true';
    } else {
      delete document.body.dataset.keyboardOpen;
    }
    return () => {
      delete document.body.dataset.keyboardOpen;
    };
  }, [isInputFocused]);

  /**
   * 位置合わせが済んだら `thread_id` / `message_id` を URL から外す。
   * `handleModeChange` や利用者の切り替えは URL のクエリを引き継ぐため、残すとタブを
   * 切り替えるたびに通知の対象へ引き戻され、送ったばかりのメッセージが見えなくなる。
   * 位置合わせはやりとりタブでしか起きないので、ハッシュは `#history` で固定する。
   */
  const handleFocusComplete = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('thread_id');
    params.delete('message_id');
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}#history`, { replace: true });
  }, [navigate, location.pathname, location.search]);

  const handleModeChange = useCallback((newMode: MessageMode) => {
    setActiveMode(newMode);
    navigate(`${location.pathname}${location.search}#${newMode}`, { replace: true });
  }, [navigate, location.pathname, location.search]);

  // 子（MessageList）の位置合わせより先に走らせるため layout effect にする。
  // 通常の effect は子から先に実行されるので、データが既に手元にあると先に handleFocusComplete が
  // URL から通知の対象を外し、そのあとここが初回の location.search で書き戻してしまう
  useLayoutEffect(() => {
    // state経由のinitialModeをハッシュに同期
    if (!modeFromHash && initialMode) {
      navigate(`${location.pathname}${location.search}#${initialMode}`, {
        replace: true,
      });
    }
    // replyTo stateの消去（既存ロジック維持）
    if (initialReplyTo) {
      window.history.replaceState({}, document.title);
    }
  }, []);

  const isSelf = selectedPatient?.relationship === '本人';

  // 送信閉鎖中に compose タブまたは #compose 直リンクで来た場合は history へ正規化
  useEffect(() => {
    if (isSendingClosed && (activeMode === 'compose' || location.hash === '#compose')) {
      if (activeMode !== 'history') {
        setActiveMode('history');
      }
      if (location.hash !== '#history') {
        navigate(`${location.pathname}${location.search}#history`, { replace: true });
      }
    }
  }, [isSendingClosed, activeMode, navigate, location.pathname, location.search, location.hash]);

  // 利用者変更時にリプライとメッセージをクリア
  const prevPatientIdRef = useRef<number | null>(selectedPatientId);
  useEffect(() => {
    if (prevPatientIdRef.current !== null && prevPatientIdRef.current !== selectedPatientId) {
      setReplyTo(null);
      setMessageText('');
      setShowSendSuccessDialog(false);
      // 返信先も解除する。残すと「切替先の patient_id × 切替元の thread_id」で送ってしまい、
      // サーバ側の照合（PR7 / S2 Step 4）で 404 になる
      setReplyTargetThreadId(null);
      setReplyMessageText('');
      lastReplyThreadIdRef.current = null;
    }
    prevPatientIdRef.current = selectedPatientId;
  }, [selectedPatientId]);

  // 本人の場合はhideFromPatientを常にfalseにリセット
  useEffect(() => {
    if (isSelf) {
      setHideFromPatient(false);
    }
  }, [selectedPatientId, isSelf]);

  // API hooks — threads 付きの全データを一括取得
  const {
    data: threadData,
    isLoading: isLoadingMessages,
    error: messagesError,
  } = useMessagesThread(selectedPatientId);

  // 確認済みステータスの軽量ポーリング（15秒間隔）
  useMessageConfirmedStatus(selectedPatientId);

  const sendMessageMutation = useSendMessage();

  // Speech recognition hook
  const {
    state: voiceState,
    lastSegment,
    segmentId,
    startRecording,
    stopRecording,
    resetRecording,
  } = useSpeechRecognition();

  const isRecording = voiceState === 'recording';
  const isProcessing = voiceState === 'processing';

  // 利用者を切り替えたら録音・文字起こしも止める。進行中の結果が遅れて届くと、
  // 切替先の利用者の入力欄に追記されてしまう
  useEffect(() => {
    resetRecording();
  }, [selectedPatientId, resetRecording]);

  // 音声入力結果を、返信の BottomSheet が開いていればそちらへ、そうでなければ入力タブへ反映（差分のみ追記）
  useEffect(() => {
    if (lastSegment) {
      const setText = replyTargetThreadId ? setReplyMessageText : setMessageText;
      setText((prev) => prev + lastSegment);
    }
  }, [segmentId]);

  // 送信閉鎖に切り替わったら返信の BottomSheet を閉じる。
  // 開いたまま残すと、閉鎖が解除されたときに意図せず開いた状態で再表示される
  useEffect(() => {
    if (!isSendingClosed) return;
    setReplyTargetThreadId(null);
    resetRecording();
  }, [isSendingClosed, resetRecording]);

  // Handlers
  const handleSend = useCallback(() => {
    if (!selectedPatientId || !messageText.trim()) return;

    sendMessageMutation.mutate(
      {
        patient_id: selectedPatientId,
        content: messageText.trim(),
        reply_to: replyTo || undefined,
        // thread_id を渡さず新規スレッドを作る（B-8 #6）
        hide_from_patient: hideFromPatient || undefined,
      },
      {
        onSuccess: () => setShowSendSuccessDialog(true),
      }
    );

    setMessageText('');
    setReplyTo(null);
    setHideFromPatient(false);
  }, [
    selectedPatientId,
    messageText,
    hideFromPatient,
    sendMessageMutation,
    replyTo,
  ]);

  /** やりとり画面のカードから返信先スレッドを選び、返信の BottomSheet を開く（B-8 #5） */
  const handleReplyToThread = useCallback((threadId: string) => {
    if (lastReplyThreadIdRef.current !== threadId) {
      setReplyMessageText('');
      lastReplyThreadIdRef.current = threadId;
    }
    setReplyTargetThreadId(threadId);
  }, []);

  const handleReplyClose = useCallback(() => {
    setReplyTargetThreadId(null);
    resetRecording();
  }, [resetRecording]);

  const handleReplySend = useCallback(() => {
    const content = replyMessageText.trim();
    if (!selectedPatientId || !replyTargetThreadId || !content) return;
    const sentThreadId = replyTargetThreadId;

    sendMessageMutation.mutate(
      {
        patient_id: selectedPatientId,
        content,
        thread_id: sentThreadId,
        // hide_from_patient は送らない。可視性はスレッドの設定を引き継ぐ（D-27）
      },
      {
        onSuccess: () => {
          // 送信待ちの間に別スレッドの下書きを書いたり、同じスレッドで書き足したりしていれば、
          // それは送った本文ではないので消さない。送ったときの下書きのままの場合だけ片付ける
          const isSentDraft =
            lastReplyThreadIdRef.current === sentThreadId &&
            replyMessageTextRef.current.trim() === content;
          if (isSentDraft) {
            setReplyMessageText('');
            lastReplyThreadIdRef.current = null;
            if (replyTargetThreadIdRef.current === sentThreadId) {
              setReplyTargetThreadId(null);
              resetRecording();
            }
          }
          setShowSendSuccessDialog(true);
        },
      }
    );
  }, [selectedPatientId, replyTargetThreadId, replyMessageText, sendMessageMutation, resetRecording]);

  const handleVoiceInput = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      resetRecording();
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording, resetRecording]);

  const handlePatientChange = useCallback(
    (index: number) => {
      selectPatientByIndex(index);
    },
    [selectPatientByIndex]
  );

  const officeName = threadData?.office_name || '事業所';

  // 患者データがない場合
  if (!linkedPatients || linkedPatients.length === 0) {
    return (
      <EmptyState
        fullPage
        title="連携しているご家族が見つかりません"
        description="事業所と連携を行ってください"
        icon="👤"
      />
    );
  }

  return (
    <PageDataBoundary ready={!isLoadingMessages}>
      <Container>
        {/* 利用者切り替えタブ */}
        {!isInputFocused && linkedPatients && (
          <PatientSwitcher
            patients={linkedPatients}
            selectedIndex={selectedPatientIndex}
            onPatientChange={handlePatientChange}
          />
        )}

        {/* モード切替タブ */}
        {!isInputFocused && (
          <ModeTabs
            activeMode={activeMode}
            onModeChange={handleModeChange}
            hideCompose={isSendingClosed}
          />
        )}

        {/* メインコンテンツ */}
        <ContentArea>
          {activeMode === 'compose' && !isSendingClosed ? (
            <ComposeView
              officeName={officeName}
              messageText={messageText}
              onMessageTextChange={setMessageText}
              hideFromPatient={hideFromPatient}
              onHideFromPatientChange={setHideFromPatient}
              onVoiceInput={handleVoiceInput}
              onSend={handleSend}
              isSending={sendMessageMutation.isPending}
              isRecording={isRecording}
              isProcessing={isProcessing}
              showHideFromPatient={!isSelf}
              onInputFocusChange={setIsInputFocused}
            />
          ) : (
            <HistoryView
              threads={
                isSelf
                  ? (threadData?.threads || []).filter(t => !t.hide_from_patient)
                  : (threadData?.threads || [])
              }
              patientId={selectedPatientId}
              onReplyToThread={handleReplyToThread}
              canReply={!isSendingClosed}
              focusThreadId={focusThreadId}
              focusMessageId={focusMessageId}
              onFocusComplete={handleFocusComplete}
              error={messagesError}
            />
          )}
        </ContentArea>

        {/* スレッドへの返信（デイサービス利用中止日翌日以降は出さない） */}
        {!isSendingClosed && (
          <ReplyBottomSheet
            isOpen={replyTargetThreadId !== null}
            onClose={handleReplyClose}
            messageText={replyMessageText}
            onMessageTextChange={setReplyMessageText}
            hideFromPatient={false}
            onHideFromPatientChange={() => {}}
            onVoiceInput={handleVoiceInput}
            onSend={handleReplySend}
            isSending={sendMessageMutation.isPending}
            isRecording={isRecording}
            isProcessing={isProcessing}
            // スレッドへの返信は可視性をスレッドから引き継ぐので、非表示設定のチェックは出さない（D-27）
            showHideFromPatient={false}
          />
        )}

        <SendSuccessDialog
          isOpen={showSendSuccessDialog}
          onClose={() => setShowSendSuccessDialog(false)}
          onConfirm={() => {
            setShowSendSuccessDialog(false);
            handleModeChange('history');
          }}
          officeName={officeName}
        />
      </Container>
    </PageDataBoundary>
  );
};

export default MessagePage;
